import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Cart } from "@insite/client-framework/Services/CartService";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import translate from "@insite/client-framework/Translate";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    orderDetails: Cart;
    firstName: String;
    lastName: String;
}

export interface OrderConfirmationOrderDetailsStyles {
    orderDetailsTitle?: TypographyProps;
    orderDetailsGridContainer?: GridContainerProps;
    orderDetailsGridItem?: GridItemProps;
    carrierStyle?: TypographyProps;
}

export const orderConfirmationOrderDetailsStyles: OrderConfirmationOrderDetailsStyles = {
    orderDetailsGridItem: {
        width: 12,
        css: css`
            display: block;
            padding: 5px;
        `,
    },
    orderDetailsTitle: {
        variant: "h5",
        as: "h2",
        css: css`
            @media print {
                font-size: 15px;
            }
            padding-top: 0;
            margin-bottom: 5px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: 20px;
                        `,
                        css`
                            font-size: 16px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    carrierStyle: {
        css: css`
            width: 48%;
            display: inline-flex;
        `
    }
};

type Props = OwnProps;
const styles = orderConfirmationOrderDetailsStyles;

const OrderConfirmationOrderDetails: FC<Props> = ({ orderDetails, firstName, lastName }) => {

    return (
        <GridContainer {...styles.orderDetailsGridContainer}>
            <GridItem {...styles.orderDetailsGridItem}>
                <Typography {...styles.orderDetailsTitle}>{translate("Order Details")}</Typography>
            </GridItem>
            <GridItem {...styles.orderDetailsGridItem}>
                <StyledWrapper>
                    <Typography as="strong">{translate("Order Number: ")}</Typography>
                    <Typography data-test-selector="orderConfirmation_orderNumber">
                        {orderDetails.erpOrderNumber ? orderDetails.erpOrderNumber : orderDetails.orderNumber}
                    </Typography>
                </StyledWrapper>
                {orderDetails.poNumber !== '' && (
                    <StyledWrapper>
                        <Typography as="strong">{translate("PO Number: ")}</Typography>
                        <Typography>{orderDetails.poNumber}</Typography>
                    </StyledWrapper>
                )}
                {!orderDetails.isGuestOrder && (
                    <StyledWrapper>
                        <Typography as="strong">{translate("Ordered By: ")}</Typography>
                        <Typography>{firstName} {lastName}</Typography>
                    </StyledWrapper>
                )}
                <StyledWrapper>
                    <Typography as="strong">{translate("Payment Type: ")}</Typography>
                    <Typography>{orderDetails.paymentMethod?.description}</Typography>
                </StyledWrapper>
                <StyledWrapper>
                    <Typography as="strong">{translate("Date Ordered: ")}</Typography>
                    <LocalizedDateTime
                        dateTime={orderDetails.orderDate}
                    />
                </StyledWrapper>
                {(orderDetails.requestedDeliveryDate || orderDetails.requestedPickupDate) && (
                    <StyledWrapper>
                        <Typography as="strong">
                            {translate("Req. Ship Date: ")}
                        </Typography>
                        <Typography
                            data-test-selector="orderConfirmation_requestedDate"
                        >
                            <LocalizedDateTime
                                dateTime={orderDetails.requestedDeliveryDateDisplay || orderDetails.requestedPickupDateDisplay}
                            />
                        </Typography>
                    </StyledWrapper>
                )}
                {orderDetails.fulfillmentMethod === FulfillmentMethod.Ship && (
                    <StyledWrapper>
                        <Typography as="strong">{translate("Carrier: ")}</Typography>
                        <Typography {...styles.carrierStyle}>{orderDetails.carrier!.description}</Typography>
                    </StyledWrapper>
                )}
                {orderDetails.fulfillmentMethod === FulfillmentMethod.Ship && (
                    <StyledWrapper>
                        <Typography as="strong">{translate("Service: ")}</Typography>
                        <Typography>{orderDetails.shipVia?.description}</Typography>
                    </StyledWrapper>
                )}
                {orderDetails.properties.shipViaAccountNumber && (
                    <StyledWrapper>
                        <Typography as="strong">{translate("Account Number: ")}</Typography>
                        <Typography>{orderDetails.properties.shipViaAccountNumber}</Typography>
                    </StyledWrapper>
                )}
            </GridItem>
        </GridContainer>
    );
};

export default OrderConfirmationOrderDetails;